import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PAGE_SIZE = 50;

const num = (value) => Number(value ?? 0).toLocaleString();

const money = (value) => {
  const asNumber = Number(value ?? 0);
  if (Number.isNaN(asNumber)) return String(value);
  return `$${asNumber.toFixed(2)}`;
};

const when = (value) => {
  if (!value) return 'never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '--';
  const days = Math.floor((Date.now() - parsed.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return parsed.toLocaleDateString();
};

const STATUS_TONE = {
  ACTIVE: 'bg-green-500/15 text-green-700 dark:text-green-400',
  PAYMENT_REQUIRED: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400',
  SUSPENDED: 'bg-red-500/15 text-red-700 dark:text-red-400',
  ERROR: 'bg-red-500/15 text-red-700 dark:text-red-400',
  PROVISIONING: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
};

/**
 * Admin: credit usage across every account.
 *
 * One aggregated request per page rather than a call per user, and a strictly
 * read-only one: the per-user balance RPC creates a billing period on first
 * touch, so reusing it here would start a cycle -- and grant free credits -- for
 * every dormant account whenever an admin opened this view.
 *
 * "Where it was spent" is the point of the expandable row: a remaining balance
 * alone does not tell an operator whether an account is burning credits on EM
 * optimization or on schema lookups.
 */
const CreditUsageOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [sortKey, setSortKey] = useState('spent_this_cycle');

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE), offset: String(offset),
      });
      if (search.trim()) params.set('search', search.trim());
      const { data: body } = await axios.get(
        `${API_URL}/api/admin/credits/usage-overview?${params}`, authHeaders());
      setData(body);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load credit usage.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, offset, search]);

  useEffect(() => { load(); }, [load]);

  const accounts = useMemo(() => {
    const rows = [...(data?.accounts || [])];
    // Client-side sort of the fetched page only. Server order is
    // most-recently-active, which is the useful default.
    if (sortKey === 'recent') return rows;
    return rows.sort((a, b) => Number(b[sortKey] ?? 0) - Number(a[sortKey] ?? 0));
  }, [data, sortKey]);

  const totals = data?.totals || {};
  const card = 'p-4 rounded-xl border border-gray-200 dark:border-gray-700 '
    + 'bg-white dark:bg-gray-800/40';
  const muted = 'text-gray-500 dark:text-gray-400';

  const stat = (label, value, hint) => (
    <div key={label} className={card}>
      <div className={`text-xs ${muted}`}>{label}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {hint && <div className={`text-xs mt-0.5 ${muted}`}>{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            User Credit Usage
          </h2>
          <p className={`text-xs ${muted}`}>
            Remaining balance, spend this cycle, and which operations consumed it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Search email, name or ID"
            aria-label="Search accounts"
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
              bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm w-56"
          />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label="Sort accounts"
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
              bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
          >
            <option value="spent_this_cycle">Top spenders (cycle)</option>
            <option value="lifetime_spent">Top spenders (lifetime)</option>
            <option value="available">Most credits left</option>
            <option value="recent">Most recently active</option>
          </select>
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300
              dark:border-gray-600 text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-600
          dark:text-red-400 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Fleet totals. Computed server-side over every account, so they do not
          shift while paging or searching. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stat('Spent this cycle', num(totals.spent_this_cycle),
          `${num(totals.operations_this_cycle)} operations`)}
        {stat('Credits remaining', num(totals.available),
          `${num(data?.accounts_with_cycle)} of ${num(data?.total_accounts)} accounts active`)}
        {stat('Lifetime spent', num(totals.lifetime_spent),
          `${num(totals.lifetime_purchased)} purchased`)}
        {stat('On-demand', num(totals.metered_active),
          `${money(totals.metered_settled)} billed this cycle`)}
      </div>

      {loading && !data && (
        <div className={`text-sm ${muted}`}>Loading usage...</div>
      )}

      {data && accounts.length === 0 && (
        <div className={`${card} text-sm ${muted}`}>
          {search.trim()
            ? 'No account matches that search.'
            : 'No account has used credits yet.'}
        </div>
      )}

      {accounts.length > 0 && (
        <div className={`${card} overflow-x-auto p-0`}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className={`text-left ${muted} border-b border-gray-200 dark:border-gray-700`}>
                <th className="py-2 px-3 font-medium">Account</th>
                <th className="py-2 px-3 font-medium text-right">Remaining</th>
                <th className="py-2 px-3 font-medium text-right">Free / Paid</th>
                <th className="py-2 px-3 font-medium text-right">Spent (cycle)</th>
                <th className="py-2 px-3 font-medium text-right">Lifetime</th>
                <th className="py-2 px-3 font-medium">On-demand</th>
                <th className="py-2 px-3 font-medium">Last used</th>
                <th className="py-2 px-3 font-medium" aria-label="Details" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => {
                const isOpen = expanded === account.user_id;
                const metered = account.metered;
                const cycleTotal = Number(account.spent_this_cycle ?? 0)
                  + Number(account.available ?? 0);
                const spentPct = cycleTotal > 0
                  ? Math.round((Number(account.spent_this_cycle ?? 0) / cycleTotal) * 100)
                  : 0;
                return (
                  <React.Fragment key={account.user_id}>
                    <tr className="border-b border-gray-100 dark:border-gray-800
                      hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {account.email || account.name || 'Unknown account'}
                        </div>
                        <div className={`text-xs font-mono ${muted}`}>
                          {String(account.user_id).slice(0, 8)}
                          {account.company_name ? ` - ${account.company_name}` : ''}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {account.has_cycle ? num(account.available) : '--'}
                        </div>
                        {!account.has_cycle && (
                          <div className={`text-xs ${muted}`}>no cycle yet</div>
                        )}
                      </td>
                      <td className={`py-2 px-3 text-right ${muted}`}>
                        {account.has_cycle
                          ? `${num(account.free_remaining)} / ${num(account.paid_remaining)}`
                          : '--'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="text-gray-900 dark:text-white">
                          {num(account.spent_this_cycle)}
                        </div>
                        {/* A bar reads faster than a percentage when scanning
                            many rows for who is close to exhausting credits. */}
                        <div className="mt-1 h-1 w-16 ml-auto rounded bg-gray-200
                          dark:bg-gray-700 overflow-hidden">
                          <div
                            className="h-full bg-brand-red"
                            style={{ width: `${Math.min(spentPct, 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className={`py-2 px-3 text-right ${muted}`}>
                        {num(account.lifetime_spent)}
                      </td>
                      <td className="py-2 px-3">
                        {metered ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            STATUS_TONE[metered.status]
                            || 'bg-gray-500/15 text-gray-600 dark:text-gray-400'}`}>
                            {metered.status === 'ACTIVE'
                              ? `${money(metered.settled)} of ${money(metered.monthly_cap)}`
                              : metered.status.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        ) : (
                          <span className={`text-xs ${muted}`}>off</span>
                        )}
                      </td>
                      <td className={`py-2 px-3 ${muted}`}>
                        {when(account.last_used_at)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button
                          onClick={() => setExpanded(isOpen ? null : account.user_id)}
                          aria-expanded={isOpen}
                          aria-label={`Where credits went for ${account.email || account.user_id}`}
                          className="text-xs text-brand-red hover:underline whitespace-nowrap"
                        >
                          {isOpen ? 'Hide' : 'Where'}
                        </button>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan="8" className="py-3 px-3">
                          <div className="text-xs font-medium text-gray-700
                            dark:text-gray-300 mb-2">
                            Where credits went this cycle
                          </div>
                          {(account.top_operations || []).length === 0 ? (
                            <div className={`text-xs ${muted}`}>
                              No metered operation ran this cycle.
                            </div>
                          ) : (
                            <div className="space-y-1.5 max-w-2xl">
                              {account.top_operations.map((op) => {
                                const share = Number(account.spent_this_cycle) > 0
                                  ? Math.round((Number(op.credits)
                                      / Number(account.spent_this_cycle)) * 100)
                                  : 0;
                                return (
                                  <div key={op.operation_key}
                                    className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-gray-900
                                        dark:text-white truncate">
                                        {op.display_name || op.operation_key}
                                      </div>
                                      <div className={`text-xs font-mono ${muted} truncate`}>
                                        {op.operation_key}
                                      </div>
                                    </div>
                                    <div className="w-32 h-1.5 rounded bg-gray-200
                                      dark:bg-gray-700 overflow-hidden">
                                      <div className="h-full bg-brand-purple"
                                        style={{ width: `${Math.min(share, 100)}%` }} />
                                    </div>
                                    <div className="text-xs text-gray-900
                                      dark:text-white w-24 text-right">
                                      {num(op.credits)} cr
                                    </div>
                                    <div className={`text-xs ${muted} w-20 text-right`}>
                                      {num(op.runs)} runs
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Top 5 only: the payload is bounded on purpose,
                                  so say so rather than implying completeness. */}
                              <div className={`text-xs pt-1 ${muted}`}>
                                Top {account.top_operations.length} operations of{' '}
                                {num(account.operations_this_cycle)} runs.
                                {metered && metered.status === 'ACTIVE' && (
                                  <> On-demand billed {money(metered.settled)}
                                    {' '}({money(metered.cap_remaining)} of cap left).</>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && (data.total_accounts || 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className={muted}>
            Showing {offset + 1}-{offset + (data.returned ?? accounts.length)} of{' '}
            {num(data.total_accounts)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              disabled={offset === 0}
              className="px-3 py-1.5 rounded-lg border border-gray-300
                dark:border-gray-600 text-gray-700 dark:text-gray-300
                disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={(data.returned ?? 0) < PAGE_SIZE}
              className="px-3 py-1.5 rounded-lg border border-gray-300
                dark:border-gray-600 text-gray-700 dark:text-gray-300
                disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditUsageOverview;
